"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModelBrightnessCorrectionState = exports.CollectionModelBrightnessCorrectionStateMutator = void 0;
const id_1 = require("../../../id");
const gamma_brightnessCorrection_1 = require("./gamma.brightnessCorrection");
function makeBrightnessCorrectionState(tag, id) {
    switch (tag) {
        case 'gamma':
            return gamma_brightnessCorrection_1.CollectionModelGammaBrightnessCorrection.state(id);
        default:
            throw new Error(`Unknown marker type: "${tag}"`);
    }
}
class CollectionModelBrightnessCorrectionStateMutator {
    constructor(state) {
        this.state = state;
    }
    getState() {
        const collectionState = this.state.collection.state;
        const captureBrightnessCorrectionKey = CollectionModelBrightnessCorrectionState.captureBrightnessCorrectionId(this.state.captureId);
        const state = collectionState.getFieldValue(captureBrightnessCorrectionKey);
        if (state == null) {
            return [];
        }
        return state.slice();
    }
    setState(state) {
        const collectionState = this.state.collection.state;
        const captureBrightnessCorrectionKey = CollectionModelBrightnessCorrectionState.captureBrightnessCorrectionId(this.state.captureId);
        collectionState.setFieldValue(captureBrightnessCorrectionKey, state);
    }
    create(tag, id = id_1.IkeId.generate()) {
        const state = makeBrightnessCorrectionState(tag, id);
        return this.createWithState(state);
    }
    remove(brightnessCorrection) {
        const brightnessCorrectionIds = this.getState();
        const index = brightnessCorrectionIds.indexOf(brightnessCorrection.id);
        if (index === -1) {
            throw new Error(`Cannot remove brightness correction. Instance "${brightnessCorrection.id}" is missing from capture "${this.state.captureId}"`);
        }
        brightnessCorrectionIds.splice(index, 1);
        this.setState(brightnessCorrectionIds);
        delete this.state.brightnessCorrections[brightnessCorrection.id];
        brightnessCorrection.Mutator.destroyState();
    }
    clear() {
        const brightnessCorrections = this.state.values();
        for (const brightnessCorrection of brightnessCorrections) {
            const brightnessCorrectionIds = this.getState();
            const index = brightnessCorrectionIds.indexOf(brightnessCorrection.id);
            if (index === -1) {
                continue;
            }
            this.remove(brightnessCorrection);
        }
    }
    createWithState(state) {
        const collectionState = this.state.collection.state;
        const captureId = this.state.captureId;
        const stateId = CollectionModelBrightnessCorrectionState.brightnessCorrectionId(captureId, state.id);
        if (collectionState.fields.get(stateId) != null) {
            throw new Error(`Brightness Correction "${stateId}" already exists`);
        }
        collectionState.setFieldValue(stateId, state);
        const brightnessCorrectionState = this.getState();
        brightnessCorrectionState.push(state.id);
        this.setState(brightnessCorrectionState);
        return state.id;
    }
}
exports.CollectionModelBrightnessCorrectionStateMutator = CollectionModelBrightnessCorrectionStateMutator;
const ID_PREFIX_BRIGHTNESS_CORRECTION = 'b';
const ID_PREFIX_CAPTURE_BRIGHTNESS_CORRECTION = 'bl';
class CollectionModelBrightnessCorrectionState {
    static brightnessCorrectionId(captureId, brightnessCorrectionId) {
        return `${ID_PREFIX_BRIGHTNESS_CORRECTION}:${captureId}:${brightnessCorrectionId}`;
    }
    static captureBrightnessCorrectionId(captureId) {
        return `${ID_PREFIX_CAPTURE_BRIGHTNESS_CORRECTION}:${captureId}`;
    }
    static getCaptureBrightnessCorrectionId(key) {
        if (key.startsWith(`${ID_PREFIX_CAPTURE_BRIGHTNESS_CORRECTION}:`)) {
            return key.substr(3);
        }
        return null;
    }
    constructor(collection, captureId) {
        this.Mutator = new CollectionModelBrightnessCorrectionStateMutator(this);
        this.id = id_1.IkeId.generate();
        this.brightnessCorrections = {};
        this.collection = collection;
        this.captureId = captureId;
    }
    hasBrightnessCorrection(brightnessCorrectionId) {
        return (this.state.indexOf(brightnessCorrectionId) > -1);
    }
    useInExportCorrection(brightnessCorrectionId) {
        if (!(this.hasBrightnessCorrection(brightnessCorrectionId))) {
            return false;
        }
        else {
            let brightness = this.getBrightnessCorrection(brightnessCorrectionId);
            switch (brightness.state.tag) {
                case 'gamma':
                    const exp = this.getBrightnessCorrection(brightnessCorrectionId).exponent;
                    return (exp !== 1);
                default:
                    return false;
            }
        }
    }
    getBrightnessCorrection(brightnessCorrectionId) {
        let brightnessCorrection = this.brightnessCorrections[brightnessCorrectionId];
        if (brightnessCorrection == null) {
            let brightnessCorrectionState = this.collection.state.getFieldValue(CollectionModelBrightnessCorrectionState.brightnessCorrectionId(this.captureId, brightnessCorrectionId)); //FIXME
            if (brightnessCorrectionState == null) {
                //if we can't find a state with the current id, we recreate a new state with the same id, and query it again
                this.Mutator.create('gamma', brightnessCorrectionId);
                brightnessCorrectionState = this.collection.state.getFieldValue(CollectionModelBrightnessCorrectionState.brightnessCorrectionId(this.captureId, brightnessCorrectionId));
            }
            switch (brightnessCorrectionState.tag) {
                case 'gamma':
                    brightnessCorrection = new gamma_brightnessCorrection_1.CollectionModelGammaBrightnessCorrection(this, brightnessCorrectionId, this.captureId);
                    break;
                default:
                    throw new Error(`Capture ${this.captureId}:Unknown brightness correction type: "${brightnessCorrectionState.tag}"`);
            }
            this.brightnessCorrections[brightnessCorrectionId] = brightnessCorrection;
        }
        return brightnessCorrection;
    }
    values() {
        const output = [];
        for (const brightnessCorrectionId of this.state) {
            const brightnessCorrection = this.getBrightnessCorrection(brightnessCorrectionId);
            output.push(brightnessCorrection);
        }
        return output;
    }
    useInExportCorrections() {
        let state = false;
        if (this.hasBrightnessCorrections()) {
            this.state.forEach(el => {
                state = this.useInExportCorrection(el);
            });
        }
        return state;
    }
    hasBrightnessCorrections() {
        return this.state.length > 0;
    }
    get capture() {
        return this.collection.state.getCapture(this.captureId);
    }
    get state() {
        return this.collection.state.getFieldValue(CollectionModelBrightnessCorrectionState.captureBrightnessCorrectionId(this.captureId)) || [];
    }
}
exports.CollectionModelBrightnessCorrectionState = CollectionModelBrightnessCorrectionState;
//# sourceMappingURL=brightnessCorrection.state.js.map